import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ContentStateColor } from '../ContentStateColor.tsx';

import { TrashButton } from './TrashButton.tsx';
import type { SuggestedContentStateProps } from './types.ts';

const i18n = defineMessages({
	betaTag: {
		id: 'content-state.space-admin.suggested-content-state.beta-tag',
		defaultMessage: 'Beta',
		description: 'Label shown as a badge indicating the feature is in beta',
	},
});

const wrapperStyles = cssMap({
	root: {
		display: 'flex',
		height: '37px',
		width: '286px',
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		alignItems: 'center',
		borderRadius: token('radius.small'),
	},
	trashHovered: {
		backgroundColor: token('color.background.danger'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width'),
		outlineColor: token('color.border.danger'),
	},
});

const stateAndButtonsStyles = cssMap({
	root: {
		display: 'flex',
		width: '100%',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
});

const iconStyles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'center',
		marginInlineEnd: token('space.200'),
	},
});

export const SuggestedContentState = ({
	spaceKey,
	name,
	color,
	restrictionLevel,
	id,
	removeState,
	hideTrash,
	icon: Icon,
	iconColor,
	showBetaTag,
}: SuggestedContentStateProps) => {
	const [isTrashHovered, setIsTrashHovered] = useState<boolean>(false);
	const resolvedIconColor = iconColor || token('color.icon.accent.blue');

	return (
		<Box
			testId="suggested-state-wrapper"
			xcss={cx(wrapperStyles.root, isTrashHovered && wrapperStyles.trashHovered)}
		>
			{Icon ? (
				<Box xcss={iconStyles.root}>
					<Icon label="" size="medium" color={resolvedIconColor} />
				</Box>
			) : (
				<ContentStateColor
					appearance="spaceAdminContentState"
					color={color}
					restrictionLevel={restrictionLevel}
				/>
			)}
			<Box testId="text-and-buttons-wrapper" xcss={stateAndButtonsStyles.root}>
				<Inline alignBlock="center" space="space.100">
					{name}
					{showBetaTag && (
						<Lozenge appearance="discovery">
							<FormattedMessage {...i18n.betaTag} />
						</Lozenge>
					)}
				</Inline>
				{!hideTrash && (
					<TrashButton
						spaceKey={spaceKey}
						name={name}
						color={color}
						id={id}
						setIsTrashHovered={setIsTrashHovered}
						removeState={removeState}
					/>
				)}
			</Box>
		</Box>
	);
};
